import { moneyToDecimalString } from '@ibe/utils';
import { applyMapping } from '../transformation-dsl.js';
const GTM_SCRIPT_ORIGIN = 'https://www.googletagmanager.com';
const GTM_NOSCRIPT_ORIGIN = 'https://www.googletagmanager.com';
export class GtmAdapter {
    provider = 'GTM';
    isLoaded = false;
    load(integration, ctx) {
        if (typeof window === 'undefined' || this.isLoaded)
            return;
        const containerId = integration.configuration.containerId;
        if (typeof containerId !== 'string' || !containerId.startsWith('GTM-')) {
            ctx.logger.warn(`GTM adapter: invalid containerId for integration ${integration.id}`);
            return;
        }
        const dataLayerName = typeof integration.configuration.dataLayerName === 'string'
            ? integration.configuration.dataLayerName
            : 'dataLayer';
        const win = window;
        const dataLayer = win[dataLayerName] ?? [];
        dataLayer.push({
            'gtm.start': new Date().getTime(),
            event: 'gtm.js',
        });
        win[dataLayerName] = dataLayer;
        const script = document.createElement('script');
        script.async = true;
        script.src = `${GTM_SCRIPT_ORIGIN}/gtm.js?id=${encodeURIComponent(containerId)}`;
        script.onerror = () => {
            window.gtm_load_error = true;
            this.isLoaded = false;
            ctx.logger.warn(`GTM adapter: failed to load ${containerId}`);
        };
        script.onload = () => {
            this.isLoaded = true;
        };
        document.head.appendChild(script);
        const noscript = document.createElement('noscript');
        const iframe = document.createElement('iframe');
        iframe.src = `${GTM_NOSCRIPT_ORIGIN}/ns.html?id=${encodeURIComponent(containerId)}`;
        iframe.height = '0';
        iframe.width = '0';
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';
        noscript.appendChild(iframe);
        document.body.appendChild(noscript);
    }
    track(event, integration, _ctx) {
        if (typeof window === 'undefined')
            return;
        const dataLayerName = typeof integration.configuration.dataLayerName === 'string'
            ? integration.configuration.dataLayerName
            : 'dataLayer';
        const win = window;
        const dataLayer = win[dataLayerName] ?? [];
        if (window.gtm_load_error === true)
            return;
        const mappings = integration.eventMappings ?? [];
        const basePayload = buildBasePayload(event);
        for (const mapping of mappings) {
            if (mapping.internalEvent !== event.kind)
                continue;
            const mapped = applyMapping(basePayload, mapping.parameterMapping);
            dataLayer.push({ event: mapping.externalEvent, ...mapped });
        }
        if (integration.configuration.legacyCompatibility === true) {
            this.legacyPush(event, dataLayer);
        }
    }
    legacyPush(event, dataLayer) {
        switch (event.kind) {
            case 'IBE_APP_MOUNTED': {
                const mounted = event;
                if (mounted.chainId !== undefined || mounted.chainName !== undefined) {
                    dataLayer.push({
                        event: 'booking',
                        ...(mounted.chainId !== undefined ? { ChainId: mounted.chainId } : {}),
                        ...(mounted.chainName !== undefined ? { ChainName: mounted.chainName } : {}),
                    });
                }
                break;
            }
            case 'IBE_SEARCH_PERFORMED':
                dataLayer.push({ event: 'booking', BookingStep: 'hotel-list' });
                break;
            case 'IBE_PROPERTY_VIEWED':
            case 'IBE_ROOM_VIEWED':
                dataLayer.push({ event: 'rooms-rates' });
                break;
            case 'IBE_ROOM_SELECTED':
                dataLayer.push({ event: 'select_item' });
                break;
            case 'IBE_CHECKOUT_STARTED':
                dataLayer.push({ event: 'add_to_cart' });
                dataLayer.push({ event: 'begin_checkout' });
                break;
            case 'IBE_GUEST_DETAILS_SUBMITTED':
                dataLayer.push({
                    event: 'add_shipping_info',
                    reservationId: event.reservationId,
                });
                break;
            case 'IBE_PAYMENT_STARTED':
                break;
            case 'IBE_BOOKING_CONFIRMED': {
                const confirmed = event;
                dataLayer.push({
                    event: 'purchase',
                    ecommerce: {
                        transaction_id: confirmed.bookingReference,
                        value: moneyToNumber(confirmed.value),
                        currency: confirmed.value.currency,
                        tax: moneyToNumber(confirmed.tax),
                        items: confirmed.items.map((item) => ({
                            item_id: item.itemId,
                            item_name: item.itemName,
                            item_category: item.itemCategory,
                            quantity: item.quantity,
                            price: moneyToNumber(item.price),
                        })),
                    },
                });
                dataLayer.push({ event: 'booking', BookingStep: 'confirmation' });
                break;
            }
        }
    }
}
function moneyToNumber(value) {
    const decimal = moneyToDecimalString(value);
    const parsed = Number.parseFloat(decimal);
    return Number.isNaN(parsed) ? 0 : parsed;
}
function buildBasePayload(event) {
    const record = { kind: event.kind };
    for (const [key, value] of Object.entries(event)) {
        if (key === 'kind')
            continue;
        if (value !== undefined) {
            record[key] = value;
        }
    }
    return record;
}
//# sourceMappingURL=gtm-adapter.js.map